import { useSortable, SortableTh } from "@/components/common/SortableTable";
/**
 * Client Profitability panel — mirrors Darren's Power BI report.
 * Layout:
 *   • Filter bar: FY / Quarter / Client / BU
 *   • Left: PPH vs GM scatter (bubble size = revenue)
 *   • Right side rail: Top/Bottom PPH by BU, Top/Bottom clients by profit
 *   • Bottom: sortable client × metrics table
 *
 * Backend: GET /api/aroflo/client-profitability
 */
import { useEffect, useMemo, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, RefreshCw, Info, AlertCircle, Download, DatabaseZap, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

function currentFYAU() {
  const now = new Date();
  return now.getMonth() + 1 >= 7 ? now.getFullYear() + 1 : now.getFullYear();
}

const FY_OPTIONS = () => {
  const cur = currentFYAU();
  return [cur, cur - 1, cur - 2];
};

export default function ClientProfitabilityPanel() {
  const [fy, setFy] = useState(currentFYAU());
  const [buCode, setBuCode] = useState("");
  const [clientFilter, setClientFilter] = useState("all");   // "all" or exact client_name
  const [clientOptions, setClientOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [loadedMultiplier, setLoadedMultiplier] = useState(1.0);
  const [savingMultiplier, setSavingMultiplier] = useState(false);
  const [data, setData] = useState(null);
  const { sorted: sortedProf, sort: profSort, setSort: setProfSort } = useSortable(data?.rows || []);
  const [err, setErr] = useState(null);

  // Load client list once for the dropdown + auto-detect an in-flight
  // sync so refreshing the page mid-sync resumes progress display.
  useEffect(() => {
    api.get("/aroflo/clients-list")
      .then((r) => setClientOptions(r.data?.clients || []))
      .catch(() => setClientOptions([]));

    // Load current loaded-cost multiplier
    api.get("/aroflo/settings/loaded-cost-multiplier")
      .then((r) => setLoadedMultiplier(Number(r.data?.multiplier ?? 1.0)))
      .catch(() => setLoadedMultiplier(1.0));

    // Resume sync polling if a background sync is already running from
    // a previous tab / session (mutex on backend keeps only one alive).
    (async () => {
      try {
        const { data: s } = await api.get("/aroflo/sync/legacy-job-costing/status");
        if (s.running) {
          setSyncBusy(true);
          toast.info("A background sync is already running — reconnecting to progress feed.");
          resumePolling();
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Extracted so both fresh clicks and page-refresh recovery reuse it.
  const resumePolling = () => {
    const start = Date.now();
    const poll = async () => {
      while (Date.now() - start < 45 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: s } = await api.get("/aroflo/sync/legacy-job-costing/status");
          if (s.running && s.progress) {
            const pg = s.progress;
            const phase = pg.phase || "starting";
            const p = pg.page || 0;
            const buckets = pg.client_fy_buckets ?? pg.invoice_client_fy_buckets ?? pg.timesheet_task_buckets ?? pg.task_client_map_size ?? 0;
            setSyncProgress(`${phase} · page ${p} · ${buckets.toLocaleString()} buckets`);
          }
          if (!s.running && s.last_result) {
            setSyncProgress(null);
            const r = s.last_result;
            if (r.ok === false) {
              toast.error(`Sync failed: ${r.error || "unknown"}`, { duration: 10000 });
            } else {
              toast.success(
                `Sync complete: ${r.records_upserted?.toLocaleString?.() ?? 0} tasks · ` +
                `${r.client_fy_buckets?.toLocaleString?.() ?? 0} client-FY rows · ` +
                `${r.invoice_pages ?? "-"} invoice pages · ` +
                `${r.timesheet_pages ?? "-"} timesheet pages` +
                (r.unlinked_timesheets ? ` · ${r.unlinked_timesheets.toLocaleString()} timesheets couldn't link to a client` : ""),
                { duration: 12000 },
              );
              setTimeout(load, 800);
            }
            setSyncBusy(false);
            return;
          }
        } catch {}
      }
      setSyncProgress(null);
      setSyncBusy(false);
      toast.warning("Sync still running after 45 minutes — check status URL manually.", { duration: 10000 });
    };
    poll();
  };

  // `selectedMonths`: array of `YYYY-MM` strings. Empty = "whole FY".
  const [selectedMonths, setSelectedMonths] = useState([]);
  // Legacy quarter param kept for the URL layer but replaced in UI.

  // Derive the 12 month options for the currently-selected FY
  // (Jul → Jun). Values are `YYYY-MM` in order.
  const monthOptionsForFY = (fyEnd) => {
    // fyEnd = 2026 means FY26 = Jul 2025 → Jun 2026
    const start = new Date(fyEnd - 1, 6, 1); // month 6 = July
    const out = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("en-AU", { month: "short", year: "2-digit" });
      out.push({ value: ym, label });
    }
    return out;
  };

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const params = { fy_year: fy };
      // Month filter takes priority — backend ignores `fy_year` when months set.
      if (selectedMonths.length > 0) params.months = selectedMonths.join(",");
      if (buCode) params.business_unit_id = buCode;
      if (clientFilter && clientFilter !== "all") params.client_id = clientFilter;
      const { data } = await api.get("/aroflo/client-profitability", { params });
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load client profitability");
    } finally { setBusy(false); }
  };
  useEffect(() => {
    // Reset selected months when the FY changes (so we don't carry
    // stale month values that belong to a different FY window).
    setSelectedMonths([]);
    // eslint-disable-next-line
  }, [fy]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy, selectedMonths, buCode, clientFilter]);

  const handleExport = () => {
    const params = new URLSearchParams({ fy_year: fy });
    if (selectedMonths.length > 0) params.set("months", selectedMonths.join(","));
    if (buCode) params.set("business_unit_id", buCode);
    if (clientFilter && clientFilter !== "all") params.set("client_id", clientFilter);
    api.get(`/aroflo/client-profitability/export.xlsx?${params.toString()}`,
            { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const cd = r.headers?.["content-disposition"] || "";
        const m = /filename="?([^"]+)"?/.exec(cd);
        a.download = m ? m[1] : `client-profitability-FY${String(fy).slice(-2)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const handleSyncJobCosting = async () => {
    setSyncBusy(true);
    try {
      toast.info("Starting Aroflo Legacy job-costing sync…");
      // Fire-and-forget — server returns in <100ms with `status: "started"`.
      const { data: kick } = await api.post(
        "/aroflo/sync/legacy-job-costing",
        null,
        { params: { years: 2 } },
      );
      if (kick?.status === "already_running") {
        toast.info("Sync already running — will refresh when it finishes.");
      } else {
        toast.success("Sync started. Polling every 5s — takes 2-4 mins on a full dataset.", { duration: 6000 });
      }

      // Poll status every 5s for up to 45 minutes — full prod sync of
      // 27k+ tasks can take 20-40 min on the sequential-page design.
      const start = Date.now();
      const poll = async () => {
        while (Date.now() - start < 45 * 60 * 1000) {
          await new Promise(r => setTimeout(r, 5000));
          try {
            const { data: s } = await api.get("/aroflo/sync/legacy-job-costing/status");
            if (s.running && s.progress) {
              const pg = s.progress;
              // Update the "syncBusy" tile with a live progress line
              const phase = pg.phase || "starting";
              const p = pg.page || 0;
              const buckets = pg.client_fy_buckets ?? pg.invoice_client_fy_buckets ?? pg.timesheet_task_buckets ?? pg.task_client_map_size ?? 0;
              setSyncProgress(`${phase} · page ${p} · ${buckets.toLocaleString()} buckets`);
            }
            if (!s.running && s.last_result) {
              setSyncProgress(null);
              const r = s.last_result;
              if (r.ok === false) {
                toast.error(`Sync failed: ${r.error || "unknown"}`, { duration: 10000 });
              } else {
                toast.success(
                  `Sync complete: ${r.records_upserted?.toLocaleString?.() ?? 0} tasks · ` +
                  `${r.client_fy_buckets?.toLocaleString?.() ?? 0} client-FY rows · ` +
                  `${r.invoice_pages ?? "-"} invoice pages · ` +
                  `${r.timesheet_pages ?? "-"} timesheet pages` +
                  (r.unlinked_timesheets ? ` · ${r.unlinked_timesheets.toLocaleString()} timesheets couldn't link to a client` : ""),
                  { duration: 12000 },
                );
                setTimeout(load, 800);
              }
              return;
            }
          } catch (e) {
            // Ignore transient poll errors — keep trying
          }
        }
        setSyncProgress(null);
        toast.warning("Sync still running after 45 minutes — check status URL manually.", { duration: 10000 });
      };
      poll().finally(() => setSyncBusy(false));
      return; // keep syncBusy true until poll completes
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Job-costing sync failed to start";
      toast.error(msg, { duration: 10000 });
      setSyncBusy(false);
    }
  };

  const coverageMissing = data && data.data_coverage?.jobs_with_revenue === 0;

  // Drill-down modal state — clicking a client-row opens this.
  const [drillClient, setDrillClient] = useState(null);

  return (
    <div className="space-y-4" data-testid="client-profitability-panel">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex-1 min-w-[240px]">
          <div className="font-semibold text-slate-800 text-sm">Client Profitability</div>
          <div className="text-[11px] text-slate-500">
            Per-client Revenue, Profit, Profit-per-Hour (PPH) and Gross Margin, sourced from Aroflo Legacy invoices + timesheets.
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            <b>Cost</b> = labour (OT rates 1.5×/2× applied) + materials (Task/Material qty × cost) + expenses (Task/Expense). Hover any Cost cell for the split.</div>
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-1">FY
          <Select value={String(fy)} onValueChange={(v) => setFy(Number(v))}>
            <SelectTrigger className="h-8 w-24 text-xs" data-testid="cp-fy-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FY_OPTIONS().map((y) => (
                <SelectItem key={y} value={String(y)}>FY{String(y).slice(-2)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <div className="text-xs text-slate-600 flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span>Months</span>
            {selectedMonths.length > 0 && (
              <button type="button"
                      onClick={() => setSelectedMonths([])}
                      className="text-[10px] text-slate-500 hover:text-slate-800 underline"
                      data-testid="cp-months-clear">
                clear (showing whole FY)
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1 max-w-[420px]" data-testid="cp-month-chips">
            {monthOptionsForFY(fy).map((m) => {
              const active = selectedMonths.includes(m.value);
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => {
                    setSelectedMonths((prev) =>
                      prev.includes(m.value)
                        ? prev.filter((x) => x !== m.value)
                        : [...prev, m.value]
                    );
                  }}
                  data-testid={`cp-month-${m.value}`}
                  className={
                    "px-2 py-0.5 rounded border text-[11px] transition " +
                    (active
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-500")
                  }
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}
                data-testid="cp-refresh-btn" className="text-xs">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
        <Button size="sm" variant="outline" onClick={handleSyncJobCosting}
                disabled={syncBusy}
                data-testid="cp-sync-job-costing-btn"
                className="text-xs gap-1"
                title="Fetch Revenue / Cost / Hours from Aroflo Legacy v1 job-costing">
          {syncBusy
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Syncing…</>
            : <><DatabaseZap className="w-3.5 h-3.5" /> Sync Job Costing</>}
        </Button>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-600 ml-1"
             title="Multiplier applied to AroFlo labour costrate on next sync. 1.0 = trust AroFlo. 1.35 = uplift 35% for super/insurance/overhead if AroFlo rates are bare payroll.">
          <label className="whitespace-nowrap">Loaded rate ×</label>
          <input type="number" step="0.05" min="1.0" max="3.0"
                 value={loadedMultiplier}
                 onChange={(e) => setLoadedMultiplier(Number(e.target.value))}
                 className="w-16 border rounded px-1 py-0.5 text-xs"
                 data-testid="cp-loaded-mult-input" />
          <button type="button"
                  onClick={async () => {
                    setSavingMultiplier(true);
                    try {
                      await api.post("/aroflo/settings/loaded-cost-multiplier", { multiplier: loadedMultiplier });
                      toast.success(`Loaded-cost multiplier set to ${loadedMultiplier}× — takes effect on next sync.`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to save multiplier");
                    } finally { setSavingMultiplier(false); }
                  }}
                  disabled={savingMultiplier}
                  data-testid="cp-loaded-mult-save"
                  className="px-1.5 py-0.5 border rounded hover:bg-slate-50 disabled:opacity-40">
            {savingMultiplier ? "…" : "Save"}
          </button>
        </div>
        <Button size="sm" variant="outline" onClick={handleExport}
                data-testid="cp-export-btn" className="text-xs gap-1"
                title="Export the current view as XLSX">
          <Download className="w-3.5 h-3.5" /> Export
        </Button>
      </div>

      {err && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>
      )}

      {syncBusy && syncProgress && (
        <div className="text-xs bg-blue-50 border border-blue-200 rounded p-3 flex gap-2"
             data-testid="cp-sync-progress">
          <Loader2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5 animate-spin" />
          <div className="flex-1">
            <div className="font-medium text-blue-900">Sync in progress</div>
            <div className="text-blue-800 mt-0.5 font-mono">{syncProgress}</div>
            <div className="text-blue-700 mt-1 text-[11px]">
              Prod's full sync (~27k tasks + tens of thousands of timesheets) can take 20-40 minutes.
              Safe to leave this tab — the sync continues on the server and you can refresh here anytime to see the current phase.
            </div>
          </div>
        </div>
      )}

      {coverageMissing && !syncBusy && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-3 flex gap-2"
             data-testid="cp-coverage-warning">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-amber-900">Revenue / Cost / Hours not yet loaded</div>
            <div className="text-amber-800 mt-0.5">
              {data.data_coverage.total_jobs_scanned.toLocaleString()} Aroflo jobs are in the hub, but the job-costing
              fields (Revenue / Cost / Hours) haven't been synced from Aroflo Legacy v1 yet. Click <b>Sync Job Costing</b>
              above (or below) to pull the last 2 years of job-costing — takes ~1-3 minutes.
            </div>
            <div className="mt-2">
              <Button size="sm" variant="outline"
                      onClick={handleSyncJobCosting} disabled={syncBusy}
                      data-testid="cp-sync-job-costing-inline-btn"
                      className="text-xs gap-1">
                {syncBusy
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Syncing…</>
                  : <><DatabaseZap className="w-3.5 h-3.5" /> Sync Job Costing Now</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Group KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard label="Revenue" value={fmtCurrency(data.total.revenue)} testid="cp-kpi-revenue" />
            <KpiCard label="Profit" value={fmtCurrency(data.total.profit)}
                     tone={data.total.profit >= 0 ? "emerald" : "rose"} testid="cp-kpi-profit" />
            <KpiCard label="PPH" value={"$" + (data.total.profit_per_hour || 0).toFixed(2)}
                     hint={`${(data.total.hours || 0).toLocaleString()} hrs`}
                     testid="cp-kpi-pph" />
            <KpiCard label="GM %" value={(data.total.gross_margin_pct || 0).toFixed(2) + "%"}
                     testid="cp-kpi-gm" />
          </div>

          {/* Main grid: scatter (left) + side rail (right) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-3"
                 data-testid="cp-scatter">
              <div className="text-xs font-semibold text-slate-700 mb-1">
                Profit Per Hour vs Gross Margin by Client
                <span className="text-slate-400 font-normal ml-2">
                  (bubble size = revenue)
                </span>
              </div>
              <PphGmScatter rows={data.rows} />
            </div>
            <div className="space-y-3">
              <SideRailList title="Top 3 PPH by Business Unit"
                            items={data.top_pph_bu}
                            format={(x) => "$" + (x.profit_per_hour || 0).toFixed(1)}
                            keyFn={(x) => x.bu_code}
                            labelFn={(x) => x.bu_code}
                            testid="cp-top-pph-bu" />
              <SideRailList title="Bottom 3 PPH by Business Unit"
                            items={data.bottom_pph_bu}
                            format={(x) => "$" + (x.profit_per_hour || 0).toFixed(1)}
                            keyFn={(x) => x.bu_code}
                            labelFn={(x) => x.bu_code}
                            testid="cp-bot-pph-bu" />
              <SideRailList title="Top 5 Clients by Profit"
                            items={data.top_clients}
                            format={(x) => fmtCompact(x.profit)}
                            keyFn={(x) => x.client_name}
                            labelFn={(x) => x.client_name}
                            testid="cp-top-clients" />
              <SideRailList title="Bottom 5 Clients by Profit"
                            items={data.bottom_clients}
                            format={(x) => fmtCompact(x.profit)}
                            keyFn={(x) => x.client_name}
                            labelFn={(x) => x.client_name}
                            testid="cp-bot-clients" />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <SortableTh label="Client" k="client_name" sort={profSort} setSort={setProfSort} className="px-3 py-2" />
                  <SortableTh label="Revenue" k="revenue" sort={profSort} setSort={setProfSort} className="px-3 py-2 text-right" />
                  <SortableTh label="Profit" k="profit" sort={profSort} setSort={setProfSort} className="px-3 py-2 text-right" />
                  <SortableTh label="PPH" k="profit_per_hour" sort={profSort} setSort={setProfSort} className="px-3 py-2 text-right" />
                  <SortableTh label="GM %" k="gross_margin_pct" sort={profSort} setSort={setProfSort} className="px-3 py-2 text-right" />
                  <SortableTh label="Hours" k="hours" sort={profSort} setSort={setProfSort} className="px-3 py-2 text-right" />
                  <SortableTh label="Jobs" k="job_count" sort={profSort} setSort={setProfSort} className="px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {sortedProf.slice(0, 100).map((r) => (
                  <tr key={r.client_name}
                      onClick={() => setDrillClient(r.client_name)}
                      className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer group"
                      title="Click to drill into this client's monthly split and task list"
                      data-testid={`cp-row-${r.client_name.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="px-3 py-1.5 text-slate-800 font-medium">
                      <span className="flex items-center gap-1">
                        {r.client_name}
                        <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-emerald-600 transition" />
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtCurrency(r.revenue)}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${r.profit < 0 ? "text-rose-700" : "text-emerald-700"}`}
                        title={
                          `Revenue: ${fmtCurrency(r.revenue)}\n` +
                          `Cost:    ${fmtCurrency(r.revenue - r.profit)}\n` +
                          `  · Labour:    ${fmtCurrency(r.labour_cost || 0)}\n` +
                          `  · Materials: ${fmtCurrency(r.material_cost || 0)}\n` +
                          `  · Expenses:  ${fmtCurrency(r.expense_cost || 0)}\n` +
                          `Profit:  ${fmtCurrency(r.profit)}`
                        }>
                      {fmtCurrency(r.profit)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                      ${(r.profit_per_hour || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                      {r.gross_margin_pct.toFixed(1)}%
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                      {r.hours.toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                      {r.job_count}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                  <td className="px-3 py-2 text-slate-800">Total ({data.rows.length} clients)</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(data.total.revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(data.total.profit)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">${(data.total.profit_per_hour || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{data.total.gross_margin_pct.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right tabular-nums">{data.total.hours.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{data.total.job_count}</td>
                </tr>
              </tfoot>
            </table>
            {data.rows.length > 100 && (
              <div className="text-[11px] text-slate-500 italic px-3 py-2 border-t border-slate-100">
                Showing top 100 of {data.rows.length} clients (sorted by revenue).
              </div>
            )}
          </div>
        </>
      )}
      {drillClient && (
        <ClientDrillDownModal
          clientName={drillClient}
          fy={fy}
          months={selectedMonths}
          buCode={buCode}
          onClose={() => setDrillClient(null)}
        />
      )}
    </div>
  );
}


function PphGmScatter({ rows }) {
  const points = useMemo(() => (rows || []).filter((r) => r.hours > 0 || r.revenue > 0), [rows]);
  if (points.length === 0) {
    return (
      <div className="text-[11px] text-slate-500 italic py-8 text-center">
        No data to plot — check filters or wait for Aroflo job-costing sync.
      </div>
    );
  }
  const W = 720, H = 360, pad = { l: 40, r: 20, t: 20, b: 30 };
  const innerW = W - pad.l - pad.r, innerH = H - pad.t - pad.b;
  const xMax = Math.max(1, ...points.map((p) => p.profit_per_hour));
  const gmVals = points.map((p) => p.gross_margin_pct);
  const yMin = Math.min(-200, ...gmVals), yMax = Math.max(100, ...gmVals);
  const rMax = Math.max(1, ...points.map((p) => p.revenue));

  const px = (v) => pad.l + (v / xMax) * innerW;
  const py = (v) => pad.t + innerH - ((v - yMin) / (yMax - yMin)) * innerH;
  const pr = (v) => 3 + 15 * Math.sqrt(Math.max(0, v) / rMax);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="360">
      {/* Y-axis gridlines */}
      {[-100, -50, 0, 50, 100].map((y) => y >= yMin && y <= yMax && (
        <g key={y}>
          <line x1={pad.l} x2={W - pad.r} y1={py(y)} y2={py(y)}
                stroke={y === 0 ? "#94a3b8" : "#f1f5f9"} strokeWidth="1"
                strokeDasharray={y === 0 ? "3,3" : ""} />
          <text x={pad.l - 4} y={py(y)} dy="3"
                textAnchor="end" className="fill-slate-400 text-[10px]">{y}%</text>
        </g>
      ))}
      {/* X-axis labels */}
      {[0, xMax * 0.25, xMax * 0.5, xMax * 0.75, xMax].map((x, i) => (
        <text key={i} x={px(x)} y={H - pad.b + 14} textAnchor="middle"
              className="fill-slate-400 text-[10px]">${Math.round(x)}</text>
      ))}
      {/* Points */}
      {points.map((p, i) => (
        <circle key={p.client_name + i}
                cx={px(Math.max(0, Math.min(xMax, p.profit_per_hour)))}
                cy={py(Math.max(yMin, Math.min(yMax, p.gross_margin_pct)))}
                r={pr(p.revenue)}
                fill="#1e40af"
                fillOpacity="0.55">
          <title>{`${p.client_name}\nRevenue ${fmtCurrency(p.revenue)}\nPPH $${p.profit_per_hour.toFixed(2)}\nGM ${p.gross_margin_pct.toFixed(1)}%`}</title>
        </circle>
      ))}
      {/* Axis labels */}
      <text x={W / 2} y={H - 4} textAnchor="middle" className="fill-slate-500 text-[10px]">
        Profit Per Hour ($)
      </text>
      <text x={12} y={pad.t + innerH / 2} textAnchor="middle"
            className="fill-slate-500 text-[10px]"
            transform={`rotate(-90 12 ${pad.t + innerH / 2})`}>
        Gross Margin %
      </text>
    </svg>
  );
}


function KpiCard({ label, value, hint, tone = "slate", testid }) {
  const cls = tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-900"
            : tone === "rose"    ? "border-rose-200 bg-rose-50 text-rose-900"
            : "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold tabular-nums mt-1">{value}</div>
      {hint && <div className="text-[10px] opacity-60 mt-0.5">{hint}</div>}
    </div>
  );
}


function SideRailList({ title, items, format, keyFn, labelFn, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">
        {title}
      </div>
      {(!items || items.length === 0) ? (
        <div className="text-[11px] text-slate-400 italic">No data</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((x) => (
            <div key={keyFn(x)} className="flex items-center justify-between text-xs">
              <span className="text-slate-700 truncate max-w-[140px]" title={labelFn(x)}>
                {labelFn(x)}
              </span>
              <span className="text-slate-800 tabular-nums font-medium">{format(x)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function fmtCompact(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return "$" + (n / 1_000_000).toFixed(2) + "M";
  if (abs >= 1_000) return "$" + (n / 1_000).toFixed(0) + "K";
  return "$" + n.toFixed(0);
}


/**
 * ClientDrillDownModal — appears when a user clicks any client-row in the
 * profitability table. Shows two panes:
 *   • Monthly split (revenue / labour / materials / expenses / hours / GM%)
 *   • Task list for the same window (job_number, title, status, site, tech,
 *     completed date). Per-task financials are not persisted so the table
 *     is a *context* view — the monthly grid is the authoritative P&L.
 */
function ClientDrillDownModal({ clientName, fy, months, buCode, onClose }) {
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setBusy(true); setErr(null);
    const params = { client_name: clientName, fy_year: fy };
    if (months && months.length > 0) params.months = months.join(",");
    if (buCode) params.business_unit_id = buCode;
    api.get("/aroflo/client-profitability/drill-down", { params })
      .then((r) => setDetail(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load drill-down"))
      .finally(() => setBusy(false));
  }, [clientName, fy, months, buCode]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" });
    } catch { return iso.slice(0, 10); }
  };
  const fmtMonth = (ym) => {
    if (!ym) return "—";
    const [y, m] = ym.split("-");
    const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${MON[parseInt(m,10)-1]} ${y.slice(-2)}`;
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden p-0"
                     data-testid="cp-drilldown-modal">
        <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
          <DialogTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            {clientName}
            <span className="text-[11px] uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
              Client Drill-Down
            </span>
            <span className="ml-2 text-[11px] text-slate-500 font-normal">
              FY{String(fy).slice(-2)}
              {months?.length > 0 ? ` · ${months.length} month${months.length>1?"s":""}` : ""}
              {buCode ? ` · BU ${buCode}` : ""}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="p-5 overflow-y-auto max-h-[calc(90vh-60px)] space-y-5">
          {busy && (
            <div className="h-40 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading detail…
            </div>
          )}
          {err && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>
          )}

          {detail && !busy && (
            <>
              {/* KPI strip — sanity totals for the selected window */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MiniKpi label="Revenue" value={fmtCurrency(detail.total.revenue)} testid="dd-kpi-revenue" />
                <MiniKpi label="Profit"
                         value={fmtCurrency(detail.total.profit)}
                         tone={detail.total.profit >= 0 ? "emerald" : "rose"}
                         testid="dd-kpi-profit" />
                <MiniKpi label="GM %"
                         value={(detail.total.gross_margin_pct || 0).toFixed(1) + "%"}
                         testid="dd-kpi-gm" />
                <MiniKpi label="Hours"
                         value={(detail.total.hours || 0).toLocaleString()}
                         hint={`${detail.task_count.toLocaleString()} task${detail.task_count===1?"":"s"} listed`}
                         testid="dd-kpi-hours" />
              </div>

              {/* Monthly split */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                   data-testid="dd-monthly">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex items-center justify-between">
                  <span>Monthly Split</span>
                  <span className="text-[10px] text-slate-500 font-normal">
                    {detail.monthly.length} month{detail.monthly.length===1?"":"s"} in window
                  </span>
                </div>
                {detail.monthly.length === 0 ? (
                  <div className="text-[11px] text-slate-500 italic px-3 py-4 text-center">
                    No monthly rows in `aroflo_client_fy_metrics` for this window — run the Aroflo Legacy sync to populate.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="px-3 py-2">Month</th>
                          <th className="px-3 py-2 text-right">Revenue</th>
                          <th className="px-3 py-2 text-right">Labour</th>
                          <th className="px-3 py-2 text-right">Materials</th>
                          <th className="px-3 py-2 text-right">Expenses</th>
                          <th className="px-3 py-2 text-right">Hours</th>
                          <th className="px-3 py-2 text-right">Profit</th>
                          <th className="px-3 py-2 text-right">GM %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.monthly.map((m) => (
                          <tr key={m.period_month}
                              className="border-t border-slate-100 hover:bg-slate-50"
                              data-testid={`dd-monthly-row-${m.period_month}`}>
                            <td className="px-3 py-1.5 text-slate-800 font-medium tabular-nums">{fmtMonth(m.period_month)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtCurrency(m.revenue)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtCurrency(m.labour_cost)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtCurrency(m.material_cost)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtCurrency(m.expense_cost)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{(m.hours || 0).toLocaleString()}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums ${m.profit < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                              {fmtCurrency(m.profit)}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{m.gross_margin_pct.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Task list */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                   data-testid="dd-tasks">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex items-center justify-between">
                  <span>Individual Tasks</span>
                  <span className="text-[10px] text-slate-500 font-normal">
                    {detail.task_count.toLocaleString()} task{detail.task_count===1?"":"s"} · sorted by most-recently-completed
                    {detail.task_count >= 500 ? " · showing first 500" : ""}
                  </span>
                </div>
                {detail.tasks.length === 0 ? (
                  <div className="text-[11px] text-slate-500 italic px-3 py-4 text-center">
                    No individual tasks match this window in `aroflo_jobs`.
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="px-3 py-2">Job #</th>
                          <th className="px-3 py-2">Title</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Site</th>
                          <th className="px-3 py-2">Technician</th>
                          <th className="px-3 py-2 text-right">Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.tasks.map((t) => (
                          <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50"
                              data-testid={`dd-task-row-${t.job_number || t.id}`}>
                            <td className="px-3 py-1 text-slate-800 font-medium tabular-nums whitespace-nowrap">{t.job_number || "—"}</td>
                            <td className="px-3 py-1 text-slate-700 max-w-[280px] truncate" title={t.title || ""}>{t.title || "—"}</td>
                            <td className="px-3 py-1"><StatusPill status={t.status} /></td>
                            <td className="px-3 py-1 text-slate-600 max-w-[180px] truncate" title={t.site_name || ""}>{t.site_name || "—"}</td>
                            <td className="px-3 py-1 text-slate-600 max-w-[160px] truncate" title={t.assigned_technician || ""}>{t.assigned_technician || "—"}</td>
                            <td className="px-3 py-1 text-right tabular-nums text-slate-500">{fmtDate(t.completed_at || t.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="text-[10px] text-slate-400 italic">
                Note: per-task financials aren't yet persisted — the Monthly Split above is the authoritative P&L for this client.
                Task list is a reference view of the jobs that contributed to the window.
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function MiniKpi({ label, value, hint, tone = "slate", testid }) {
  const cls = tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-900"
            : tone === "rose"    ? "border-rose-200 bg-rose-50 text-rose-900"
            : "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-semibold tabular-nums mt-1">{value}</div>
      {hint && <div className="text-[10px] opacity-60 mt-0.5">{hint}</div>}
    </div>
  );
}


function StatusPill({ status }) {
  if (!status) return <span className="text-slate-400 text-[11px]">—</span>;
  const s = String(status).toLowerCase();
  let cls = "bg-slate-100 text-slate-700 border-slate-200";
  if (s.includes("complet") || s.includes("closed") || s.includes("archived")) cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
  else if (s.includes("progress") || s.includes("in ")) cls = "bg-blue-50 text-blue-800 border-blue-200";
  else if (s.includes("hold") || s.includes("pending")) cls = "bg-amber-50 text-amber-800 border-amber-200";
  else if (s.includes("cancel")) cls = "bg-rose-50 text-rose-800 border-rose-200";
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${cls} whitespace-nowrap`}>
      {status}
    </span>
  );
}
